import { useState, useEffect } from 'react';

/**
 * Hook que retorna a altura real do viewport, resolvendo o bug do 100vh no iOS.
 *
 * No iOS Safari/Chrome (ambos usam WebKit), `100vh` inclui a área atrás da
 * barra de endereço e toolbar do browser, fazendo com que o conteúdo seja
 * renderizado fora da área visível.
 *
 * Este hook usa `window.innerHeight` (que reflete a área visível real) e
 * atualiza quando o viewport muda (resize, orientação, barra de endereço).
 *
 * @returns {string} Valor CSS para height (ex: "732px")
 */
export function useAppHeight() {
  const [appHeight, setAppHeight] = useState(() => {
    if (typeof window !== 'undefined') {
      return `${window.innerHeight}px`;
    }
    return '100vh';
  });

  useEffect(() => {
    const updateHeight = () => {
      const vh = window.innerHeight;
      setAppHeight(`${vh}px`);
      // Também setar como CSS custom property para uso em CSS puro
      document.documentElement.style.setProperty('--app-height', `${vh}px`);
    };

    // Setar imediatamente
    updateHeight();

    // Atualizar no resize (inclui mudança de barra de endereço no iOS)
    window.addEventListener('resize', updateHeight);

    // iOS: orientationchange é mais confiável que resize para mudança de orientação
    window.addEventListener('orientationchange', () => {
      // Delay para esperar o browser recalcular o layout após orientação
      setTimeout(updateHeight, 100);
      setTimeout(updateHeight, 300);
    });

    // iOS Safari: visualViewport API é a mais precisa para viewport dinâmico
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateHeight);
    }

    return () => {
      window.removeEventListener('resize', updateHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateHeight);
      }
    };
  }, []);

  return appHeight;
}
